import { useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiEndpoints, routesPaths } from '@selfserve/react-utils';

import { usePost } from './use-api';
import { useStorage, storage, storageKeys } from './use-storage';
import { useLog } from './use-log';

export const useLogoutRequest = () => usePost<any, any>(apiEndpoints.logout);

interface IReturn {
  token: string;
  setToken: (token: string, rememberMe?: boolean) => void;
  clearToken: () => void;
  logout: (callback?: () => void) => void;
}

export const useAuth = (): IReturn => {
  const { log } = useLog();
  const navigate = useNavigate();
  const { mutateAsync: handleLogoutRequest } = useLogoutRequest();
  const [, setPartnerId] = useStorage(storage.LOCAL, storageKeys.PARTNER_ID);
  const [, setQueryParams] = useStorage(storage.LOCAL, storageKeys.QUERY_PARAMS);
  const [sessionToken, setSessionToken] = useStorage(
    storage.SESSION,
    storageKeys.TOKEN
  );
  const [localToken, setLocalToken] = useStorage(
    storage.LOCAL,
    storageKeys.TOKEN
  );
  const token = useMemo(
    () => sessionToken || localToken,
    [sessionToken, localToken]
  );

  const setToken = useCallback(
    (token: string, rememberMe?: boolean) => {
      clearToken();
      setQueryParams(null);
      if (rememberMe) {
        setLocalToken(token);
      } else {
        setSessionToken(token);
      }
    },
    [setSessionToken, setLocalToken]
  );

  const clearToken = useCallback(() => {
    setSessionToken('');
    setLocalToken('');
  }, [setSessionToken, setLocalToken]);

  const logout = useCallback(
    (callback?: () => void) => {
      if (typeof callback === 'function') callback();
      clearToken();
      setPartnerId(null);
      setQueryParams(null);
      handleLogoutRequest({});
      navigate(routesPaths.LOGIN);
    },
    [clearToken, handleLogoutRequest, navigate]
  );

  useEffect(() => {
    log('sessionToken: ', sessionToken);
    log('localToken: ', localToken);
  }, [sessionToken, localToken, log]);

  return { token, setToken, clearToken, logout };
};
